import { useState } from "react";
import type { FormEvent } from "react";
import { teaTalkList } from "@/mocks/homeData";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahht5bp14a1h3mr6nbg";

export default function TeaTalk() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("website_alt");

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg || "提交失败，请稍后再试。"));
      }
    } catch {
      setStatus("error");
      setFormError("网络出了点问题，请稍后再试。");
    }
  };

  return (
    <section id="teatalk" className="w-full border-b border-background-200/70 bg-background-50">
      <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-accent-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-cup-line text-sm" />
              </span>
              Tea Talk
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              茶余饭后谈资
            </h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-foreground-600 md:text-base">
              不用追热点追得那么累，这里把值得聊两句的内容挑出来。泡杯茶，慢慢翻。
            </p>

            <ul className="mt-7 flex flex-col divide-y divide-background-200/80 border-y border-background-200/80">
              {teaTalkList.map((item) => (
                <li key={item.id}>
                  <a
                    href="#comments"
                    className="group flex items-center justify-between gap-4 py-4"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                        <i className="ri-chat-quote-line text-base" />
                      </span>
                      <span className="text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600 md:text-[15px]">
                        {item.title}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                      {item.tag}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <div id="subscribe" className="scroll-mt-24 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-mail-send-line text-xl" />
              </span>
              <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">
                订阅今日谈资
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                留个邮箱，每天把最值得闲聊的瓜整理好送到你手边，不刷屏、不灌水。
              </p>

              <form
                data-readdy-form
                id="subscribe-talk"
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-3 sm:flex-row"
              >
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="你的邮箱地址"
                  className="h-11 flex-1 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
                <input
                  className="form_control_alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="h-11 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {status === "loading" ? "提交中…" : "订阅谈资"}
                </button>
              </form>

              {status === "success" && (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-checkbox-circle-line text-base" />
                  </span>
                  订阅成功，明天见。今天的瓜也别错过，往下翻翻。
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-error-warning-line text-base" />
                  </span>
                  {formError}
                </p>
              )}

              <p className="mt-4 text-xs text-foreground-400">
                我们只发送谈资内容，随时可以退订。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}